import { useState, useEffect, useCallback, useRef } from "react";
import { Calendar, ExternalLink, Clock, RefreshCw } from "lucide-react";
import { useStore } from "../store/useStore";

interface CalendarEvent {
  title: string;
  start: string | null;
  end: string | null;
  allDay: boolean;
  location: string | null;
  htmlLink: string | null;
}

interface CalendarResponse {
  events?: CalendarEvent[];
  refreshed_token?: string;
  refreshed_token_expires_in?: number | null;
  error?: string;
  code?: string;
}

export default function CalendarSidebar() {
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const session = useStore((s) => s.session);

  // After the function refreshes the Google access token, prefer it for
  // subsequent fetches in this mount. Survives re-renders, dropped on unmount —
  // the next page load will hit the refresh path again, which is cheap.
  const refreshedAccessToken = useRef<string | null>(null);

  const fetchCalendarEvents = useCallback(async () => {
    if (!session?.access_token) return;

    setLoading(true);
    setError(null);

    const accessToken = refreshedAccessToken.current ?? session.provider_token;
    if (!accessToken) {
      setError("No calendar access. Re-sign in to grant calendar permissions.");
      setLoading(false);
      return;
    }

    const baseUrl = import.meta.env.VITE_SUPABASE_URL as string | undefined;
    if (!baseUrl) {
      setError("Calendar service not configured.");
      setLoading(false);
      return;
    }

    let res: Response;
    try {
      res = await fetch(
        `${baseUrl}/functions/v1/google-calendar?google_token=${encodeURIComponent(accessToken)}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
        }
      );
    } catch {
      setError("Couldn't reach the calendar service. Check your connection.");
      setLoading(false);
      return;
    }

    let body: CalendarResponse | null = null;
    try {
      body = (await res.json()) as CalendarResponse;
    } catch {
      // fall through with body=null; handled below.
    }

    if (!res.ok) {
      if (res.status === 401) {
        // Both TOKEN_EXPIRED (no refresh path available — user signed in
        // before we had refresh-token capture) and REFRESH_FAILED (refresh
        // token revoked / expired) resolve the same way: re-sign-in.
        setError(
          "Your Google session expired. Please sign out and back in to refresh calendar access."
        );
      } else if (res.status === 403 || body?.code === "INSUFFICIENT_SCOPE") {
        setError(
          "Calendar permission was revoked. Sign in again to re-grant access."
        );
      } else {
        setError(`Couldn't load calendar (HTTP ${res.status}).`);
      }
      setLoading(false);
      return;
    }

    if (body?.refreshed_token) {
      refreshedAccessToken.current = body.refreshed_token;
    }
    setEvents(body?.events || []);
    setLoading(false);
  }, [session]);

  useEffect(() => {
    fetchCalendarEvents();
  }, [fetchCalendarEvents]);

  const formatTime = (dateStr: string | null): string => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  };

  return (
    <div className="bg-surface border border-border rounded-lg p-4">
      <div className="flex items-center gap-2 mb-3">
        <Calendar className="w-4 h-4 text-primary" />
        <h3 className="text-sm font-medium text-foreground">Today's Calendar</h3>
      </div>

      {loading ? (
        <div className="space-y-2">
          {[1, 2].map((i) => (
            <div key={i} className="h-12 shimmer rounded" />
          ))}
        </div>
      ) : error ? (
        <div className="space-y-2">
          <p className="text-xs text-muted-foreground">{error}</p>
          <button
            onClick={fetchCalendarEvents}
            className="inline-flex items-center gap-1 text-xs text-primary hover:text-primary/80 transition-colors"
          >
            <RefreshCw className="w-3 h-3" />
            Retry
          </button>
        </div>
      ) : events.length === 0 ? (
        <p className="text-xs text-muted-foreground">No events today</p>
      ) : (
        <div className="space-y-2">
          {events.map((event, i) => (
            <div
              key={i}
              className="flex items-start gap-2 py-1.5"
            >
              <Clock className="w-3.5 h-3.5 text-muted-foreground mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-foreground truncate">{event.title}</p>
                <p className="text-xs text-muted-foreground">
                  {event.allDay
                    ? "All day"
                    : `${formatTime(event.start)} - ${formatTime(event.end)}`}
                </p>
              </div>
              {event.htmlLink && (
                <a
                  href={event.htmlLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-muted-foreground hover:text-primary flex-shrink-0"
                  onClick={(e) => e.stopPropagation()}
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                </a>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
